import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import LoadingSpinner from '@/components/LoadingComponent';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface LoginFormProps {
  onLogin: (email: string, demographicsConsent: boolean) => Promise<void>;
}

// Variantes de animação para o LoginForm
const formVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeInOut" } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.5, ease: "easeInOut" } }
};

export const LoginForm = ({ onLogin }: LoginFormProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [demographicsConsent,] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');

    if (emailParam) {
      handleLogin(emailParam);
    }
  }, [demographicsConsent]); // Adicione demographicsConsent como dependência

  useEffect(() => {
    console.log('Estado do demographicsConsent mudou para:', demographicsConsent);
  }, [demographicsConsent]);

  const handleLogin = async (emailToLogin: string) => {
    setLoading(true);
    setError('');

    try {
      await onLogin(emailToLogin, demographicsConsent);
      navigate('/');
    } catch (err: any) {
      if (err.message === 'banned') {
        setError('Acesso negado. Sua conta foi suspensa.');
      } else {
        setError('E-mail não encontrado ou inválido. Por favor, verifique o link.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      handleLogin(email);
    }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-gray-900 px-4"
        variants={formVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <Card className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl overflow-hidden">
          <CardContent className="p-6">
            {/* Header do Email */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center">
                <img
                  src="https://app.thenewscc.com.br/square_logo_favicon.png"
                  alt="Coffee Cup"
                  className="w-6 h-6"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-800 dark:text-gray-200">the news</span>
                  <span className="text-sm text-gray-500">
                    06:06
                  </span>
                </div>
                <div className="text-sm text-gray-500">para: você@email.com</div>
              </div>
            </div>

            <img
              src="https://app.thenewscc.com.br/email-header.jpg"
              alt="Header"
              className="object-cover rounded-t-lg rounded-b-none mb-6"
            />

            {/* Conteúdo do Form */}
            <div className="space-y-6">
              <div className="text-left space-y-2">
                <h1 className="text-3xl font-bold">
                  <span className="text-gray-800 dark:text-gray-200">seja bem vindo(a)</span>
                </h1>
                <p className="text-gray-600 dark:text-gray-400 text-xs font-extrabold">
                  PROGRAMA DE INDICAÇÃO
                </p>
              </div>

              {error && (
                <Alert 
                  variant={error.includes('suspensa') ? "destructive" : "destructive"} 
                  className={error.includes('suspensa') ? "bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700" : "bg-red-50 dark:bg-red-800 border-red-200"}
                >
                  <AlertDescription className="text-red-600 dark:text-red-200">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              {loading ? (
                <div className="flex justify-center p-4">
                  <LoadingSpinner />
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="email"
                    placeholder="Digite seu e-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="border-gray-300 dark:border-gray-600"
                  />

                  <Button 
                    type="submit" 
                    className="bg-yellow-400 text-black dark:text-white px-6 py-3 rounded-lg font-medium hover:bg-yellow-500 transition-colors w-auto"
                  >
                    acessar
                  </Button>
                </form>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};